import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete, Button, Card, Menu, MenuItem, Theme, styled, useMediaQuery, Box, Typography, Paper, Chip, IconButton } from '@mui/material';
import { departmentsApi, shiftTemplateApi } from 'api';
import { CustomDialog } from 'components/CustomDialog';
import FlexBox from 'components/flexbox/FlexBox';
import { useState, type FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { ShiftTemplateEntity } from 'api/generated';
import AddShiftTemplateModal from 'page-sections/data-table/dataTableV2/AddShiftTemplateModal';
import { Department } from 'pages/dashboards/dashboard-main-page';
import SearchInput from 'components/input-fields/SearchInput';
import { searchByName } from 'utils/utils';
import AppTextField from 'components/input-fields/AppTextField';
import FlexBetween from 'components/flexbox/FlexBetween';
import { checkTokenRole } from 'utils/checkToken';
import { useSeason } from 'contexts/SeasonContext';
import CopyShiftTemplateModal from 'page-sections/data-table/dataTableV2/CopyShiftTemplateModal';

// styled components
const ButtonWrapper = styled(FlexBox)(({ theme }) => ({
    [theme.breakpoints.down(500)]: {
        marginTop: 10,
        width: '100%',
        flexDirection: 'column-reverse',
        '& > .MuiBox-root': {
            width: '100%',
            margin: '10px 0',
            alignItems: 'center',
            flexDirection: 'column',
        },
        '& .MuiButton-root': { minWidth: '100%' },
    },
}));


const ShiftsTable: FC = () => {
    const [tableData, setTableData] = useState<ShiftTemplateEntity[]>([]);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [shiftTemplateData, setShiftTemplateData] = useState<ShiftTemplateEntity | null>(null);
    const { t } = useTranslation();
    const [departments, setDepartments] = useState<Department[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; id: number; } | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const [filteredItem, setFilteredItem] = useState(tableData);
    const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down(1200));
    const isDepChief = checkTokenRole() === 'DEPCHIEF';
    const { seasonId } = useSeason();
    const [openCreateMenu, setOpenCreateMenu] = useState(false);
    const [openCopyTemplateModal, setOpenCopyTemplateModal] = useState(false);

    const fetchTableData = async () => {
        (isDepChief ? shiftTemplateApi.findAllWhereIsBoss(seasonId) : shiftTemplateApi.findAll())
            .then(({ data }) => {
                setTableData(data.filter((shiftTemplate) => shiftTemplate.generatedFromShiftTemplateId === -1));
            })
            .catch((error) => {
                console.log(error);
            });
    }

    const handleOpenDeleteConfirmation = () => {
        setDeleteConfirmationOpen(true);
    };

    const handleCloseDeleteConfirmation = () => {
        setDeleteConfirmationOpen(false);
    };

    useEffect(() => {
        fetchTableData();
        fetchAllDepartments();
    }, []);

    const fetchAllDepartments = () => {
        (isDepChief ? departmentsApi.findWhereIsBoss(seasonId) : departmentsApi.findAll()).then(({ data: departments }) => {
            setDepartments(departments);
        });
    }


    const ids = selectedRows.map((item: any) => item.original.id);
    const handleDelete = async () => {
        handleOpenDeleteConfirmation();
    };

    const handleConfirmDelete = async () => {
        const deleteActions = ids.map(async (id) => shiftTemplateApi.remove(id));
        await Promise.all(deleteActions);
        fetchTableData();
        // Close confirmation dialog
        handleCloseDeleteConfirmation();
    };

    const handleCloseModal = () => { setOpen(false) }

    const handleAddShiftTemplate = (data?: ShiftTemplateEntity | null) => {
        setShiftTemplateData(data ?? null);
        setOpen(true);
    };

    const applyFilters = () => {
        let filteredData = tableData;
        if (selectedDepartment) {
            filteredData = filteredData.filter((item: any) => item.departmentId === selectedDepartment.id);
        }
        if (searchValue) {
            filteredData = searchByName(filteredData, searchValue);
        }
        setFilteredItem(filteredData);
    };

    // Group templates by department
    const groupedTemplates = useMemo(() => {
        const grouped: Record<number, { department: Department; templates: ShiftTemplateEntity[] }> = {};
        
        filteredItem.forEach((template) => {
            const deptId = template.departmentId;
            if (!grouped[deptId]) {
                const dept = departments.find(d => d.id === deptId);
                if (dept) {
                    grouped[deptId] = {
                        department: dept,
                        templates: []
                    };
                }
            }
            if (grouped[deptId]) {
                grouped[deptId].templates.push(template);
            }
        });
        
        return Object.values(grouped);
    }, [filteredItem, departments]);

    useEffect(() => {
        applyFilters();
    }, [searchValue, selectedDepartment, tableData]);

    const menuAnchor = document.getElementById('createReportButton');

    return (
        <>
            <AddShiftTemplateModal
                key={new Date().getTime()} // Needed to update modal if it opens with the same data 
                open={open}
                onClose={handleCloseModal}
                edit={!!shiftTemplateData}
                data={shiftTemplateData}
                setData={setShiftTemplateData}
                fetchData={fetchTableData}
                departmentsData={departments}
                openedFrom="shifts-table"
            />
            <CopyShiftTemplateModal
                fetchTemplates={fetchTableData}
                onClose={() => { setOpenCopyTemplateModal(false) }}
                open={openCopyTemplateModal}
                shiftTemplates={tableData}
                openShiftTemplate={handleAddShiftTemplate}
            />
            <CustomDialog
                open={deleteConfirmationOpen}
                onClose={handleCloseDeleteConfirmation}
                onConfirm={handleConfirmDelete}
                title={t('dialog.title', {
                    item: t('shiftTemplate.itemName'),
                })}
                content={t('dialog.content')}
                icon={<DeleteForeverIcon />}
                confirmText={t('dialog.confirm')}
                confirmColor='error'
                cancelColor='primary'
            />

            <FlexBetween flexWrap='wrap' mt={2}>
                <FlexBox width={downMd ? '100%' : 'auto'} flexBasis="600px" gap={2} alignItems={'center'} >
                    <SearchInput
                        bordered={false}
                        placeholder={t('shiftTemplate.searchTemplate')}
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                        }}
                    />
                    <Autocomplete
                        id="department-selector"
                        options={departments?.map((department) => ({
                            label: department.name,
                            id: department.id
                        })) || []}
                        value={selectedDepartment}
                        onChange={(event, value) => {
                            setSelectedDepartment(value ? { label: value.label, id: value.id } : null);
                        }}
                        renderInput={(params) =>
                            <AppTextField
                                {...params}
                                placeholder={t('department.departmentSelector.select')}
                                label={t('department.itemName')}
                            />
                        }
                        fullWidth
                        isOptionEqualToValue={(option, value) => option.id === value?.id} // It serves to prevent alerts/errors mui in the console
                    />
                </FlexBox>
                <ButtonWrapper alignItems='center'>
                    <FlexBox gap={2} justifyContent={'end'} width={'100%'}>
                        {selectedRows.length > 0 && (
                            <Button
                                size='small'
                                color='error'
                                variant='contained'
                                onClick={handleDelete}
                                sx={{ color: 'common.white' }}
                            >
                                {t('common.tables.select.deleteSelected')}
                            </Button>
                        )}
                        <Button
                            endIcon={<AddIcon />}
                            variant='contained'
                            color='primary'
                            id='createReportButton'
                            onClick={() => { setOpenCreateMenu(true) }}
                            sx={{ fontSize: 'small' }}
                        >
                            {t('common.tables.button.addItem', {
                                item: t('shiftTemplate.itemName'),
                            })}
                        </Button>
                        <Menu
                            anchorEl={menuAnchor}
                            open={openCreateMenu}
                            onClose={() => { setOpenCreateMenu(false) }}
                            slotProps={{
                                paper: {
                                    elevation: 0,
                                    sx: {
                                        overflow: 'visible',
                                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                        mt: 1.5,
                                        '& .MuiAvatar-root': {
                                            width: 32,
                                            height: 32,
                                            ml: -0.5,
                                            mr: 1,
                                        },
                                        '&:before': {
                                            content: '""',
                                            display: 'block',
                                            position: 'absolute',
                                            top: 0,
                                            left: 'calc(50% - 5px)',
                                            width: 10,
                                            height: 10,
                                            bgcolor: 'background.paper',
                                            transform: 'translateY(-50%) rotate(45deg)',
                                            zIndex: 0,
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem
                                onClick={() => { handleAddShiftTemplate() }}
                                sx={{
                                    width: '210px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    paddingRight: '20px',
                                }}
                            >
                                {t('common.tables.button.addItem', {
                                    item: t('shiftTemplate.itemName'),
                                })} <AddIcon fontSize='small' sx={{ display: 'flex', ml: 'auto' }} />
                            </MenuItem>

                            <MenuItem
                                onClick={() => { setOpenCopyTemplateModal(true) }}
                                sx={{
                                    width: '210px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    paddingRight: '20px',
                                }}
                            >
                                {t('shiftTemplate.copyFrom')} <ContentCopyIcon fontSize='small' sx={{ display: 'flex', ml: 'auto' }} />
                            </MenuItem>
                        </Menu>
                    </FlexBox>
                </ButtonWrapper>
            </FlexBetween>
            <Box sx={{ marginTop: 2 }}>
                {groupedTemplates.length === 0 ? (
                    <Card sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('shiftTemplate.noTemplatesFound')}
                        </Typography>
                    </Card>
                ) : (
                    groupedTemplates.map((group) => (
                        <Card key={group.department.id} sx={{ mb: 1.5, overflow: 'hidden' }}>
                            <Box
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    p: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '0.95rem' }}>
                                    {group.department.name}
                                </Typography>
                                <Chip
                                    label={`${group.templates.length} ${t('shiftTemplate.itemName')}`}
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', height: '20px', fontSize: '0.7rem' }}
                                    size="small"
                                />
                            </Box>
                            <Box sx={{ p: 1 }}>
                                {group.templates.map((template, index) => (
                                    <Paper
                                        key={template.id}
                                        elevation={0}
                                        sx={{
                                            p: 1,
                                            mb: index < group.templates.length - 1 ? 1 : 0,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                                boxShadow: 1
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <FlexBetween>
                                            <Box flex={1} minWidth={0}>
                                                <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: '0.85rem', mb: 0.25 }}>
                                                    {template.name}
                                                </Typography>
                                                {template.description && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                                        {template.description}
                                                    </Typography>
                                                )}
                                                <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                                                    {template.days && template.days.length > 0 && (
                                                        <Chip
                                                            label={`${template.days.length} ${template.days.length !== 1 ? t('common.days') : t('common.day')}`}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ height: '18px', fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                                                        />
                                                    )}
                                                    {template.shifts && template.shifts.length > 0 && (
                                                        <Chip
                                                            label={`${template.shifts.length} ${template.shifts.length !== 1 ? t('shift.itemNamePlural') : t('shift.itemName')}`}
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            sx={{ height: '18px', fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                            <Box display="flex" gap={0.5} ml={1.5} flexShrink={0}>
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => handleAddShiftTemplate(template)}
                                                    sx={{
                                                        '&:hover': {
                                                            bgcolor: 'primary.lighter'
                                                        },
                                                        padding: '4px'
                                                    }}
                                                >
                                                    <EditIcon sx={{ fontSize: '18px' }} />
                                                </IconButton>
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedRows([{ original: template } as any]);
                                                        handleDelete();
                                                    }}
                                                    sx={{
                                                        '&:hover': {
                                                            bgcolor: 'error.lighter'
                                                        },
                                                        padding: '4px'
                                                    }}
                                                >
                                                    <DeleteIcon sx={{ fontSize: '18px' }} />
                                                </IconButton>
                                            </Box>
                                        </FlexBetween>
                                    </Paper>
                                ))}
                            </Box>
                        </Card>
                    ))
                )}
            </Box>
        </>
    );
}

export default ShiftsTable;